import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import {
  convertDiagnosesFlatAnswersToFormData,
  convertOtherDiagnosesToFlatAnswers,
  convertPrimaryDiagnosisToFlatAnswers
} from '../../../../../helpers.uncontrolled'

/**
 * Background subsection adapter
 * Handles diagnoses, equipment, allergies, and other background medical info (12 fields)
 */
export class BackgroundAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract diagnosis data using existing helper
    const { primaryDiagnosis, otherDiagnoses } = convertDiagnosesFlatAnswersToFormData(
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS
      ),
      getAnswerValuesFromItems(
        flatAnswersArray,
        DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS
      )
    )

    // Direct field extractions
    const backgroundEquipment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT
    )

    const backgroundEquipmentOther = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER
    )?.at(0)

    const backgroundDmeProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER
    )?.at(0)

    const backgroundAllergies = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES
    )

    const backgroundAllergiesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES
    )?.at(0)

    const backgroundWaivers = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS
    )

    const backgroundAdditionalServices = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES
    )

    const backgroundPrognosis = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS
    )?.at(0)

    const backgroundDnr = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR
    )?.at(0)

    const backgroundAdvanceDirectives = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES
    )?.at(0)

    const backgroundAdvanceDirectivesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES
    )?.at(0)

    return {
      [DischargeFormField.BACKGROUND_PRIMARY_DIAGNOSIS]: primaryDiagnosis,
      [DischargeFormField.BACKGROUND_OTHER_DIAGNOSIS]: otherDiagnoses,
      [DischargeFormField.BACKGROUND_EQUIPMENT]: backgroundEquipment,
      [DischargeFormField.BACKGROUND_EQUIPMENT_OTHER]: backgroundEquipmentOther,
      [DischargeFormField.BACKGROUND_DME_PROVIDER]: backgroundDmeProvider,
      [DischargeFormField.BACKGROUND_ALLERGIES]: backgroundAllergies,
      [DischargeFormField.BACKGROUND_ALLERGIES_NOTES]: backgroundAllergiesNotes,
      [DischargeFormField.BACKGROUND_WAIVERS]: backgroundWaivers,
      [DischargeFormField.BACKGROUND_ADDITIONAL_SERVICES]: backgroundAdditionalServices,
      [DischargeFormField.BACKGROUND_PROGNOSIS]: backgroundPrognosis,
      [DischargeFormField.BACKGROUND_DNR]: backgroundDnr,
      [DischargeFormField.BACKGROUND_ADVANCE_DIRECTIVES]: backgroundAdvanceDirectives,
      [DischargeFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES]: backgroundAdvanceDirectivesNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_PRIMARY_DIAGNOSIS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS,
        value: convertPrimaryDiagnosisToFlatAnswers(
          this.getFormDataField(DischargeFormField.BACKGROUND_PRIMARY_DIAGNOSIS)
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_OTHER_DIAGNOSIS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS,
        value: convertOtherDiagnosesToFlatAnswers(
          this.getFormDataField(DischargeFormField.BACKGROUND_OTHER_DIAGNOSIS) ?? []
        )
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_EQUIPMENT)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_EQUIPMENT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_EQUIPMENT_OTHER)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_EQUIPMENT_OTHER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_DME_PROVIDER)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_DME_PROVIDER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_ALLERGIES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_ALLERGIES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_ALLERGIES_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_ALLERGIES_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_WAIVERS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_WAIVERS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_ADDITIONAL_SERVICES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_ADDITIONAL_SERVICES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_PROGNOSIS)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_PROGNOSIS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_DNR)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_DNR)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_ADVANCE_DIRECTIVES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_ADVANCE_DIRECTIVES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES,
        value: this.getFormDataField(DischargeFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES)
      })
    }

    return mutations
  }
}
